import { receivedTypingData, receivedStatusData } from "store/conversations"
import { channels } from "constants/cable"
import DOMPurify from "dompurify"

const moment = require("moment")

export const groupMessagesByDateAndAuthor = (messages) => {
  const groupedMessages = messages.reduce((acc, message) => {
    const messageDate = new Date(message.created_at).toISOString().split("T")[0]

    if (!acc[messageDate]) {
      acc[messageDate] = []
    }
    acc[messageDate].push(message)

    return acc
  }, {})

  const result = []
  let previousMessageDate = null

  Object.keys(groupedMessages).forEach((date) => {
    const messagesForDay = groupedMessages[date]

    let currentAuthorMessages = []

    messagesForDay.forEach((message, index) => {
      const isFirstMessage = index === 0
      const isNewDay = date !== previousMessageDate

      const messageWithFlags = {
        ...message,
        isFirstMessageOfNewDay: isNewDay
      }

      previousMessageDate = date

      if (isFirstMessage || message?.author?.id !== messagesForDay[index - 1]?.author?.id) {
        if (currentAuthorMessages.length > 0) {
          const firstMessage = currentAuthorMessages[0]
          result.push({
            ...firstMessage,
            nestedMessages: currentAuthorMessages.slice(1)
          })
          currentAuthorMessages = []
        }

        const groupedMessage = {
          ...messageWithFlags,
          nestedMessages: [],
          firstMessageOfTheDate: date
        }

        currentAuthorMessages.push(groupedMessage)
      } else {
        currentAuthorMessages.push(messageWithFlags)
      }
    })

    if (currentAuthorMessages.length > 0) {
      const firstMessage = currentAuthorMessages[0]
      result.push({
        ...firstMessage,
        nestedMessages: currentAuthorMessages.slice(1)
      })
    }
  })

  return result
}

export const beforeAction = (action) => {
  console.log("Before action:", action)
  // Add any pre-processing logic here
}

export const afterAction = (action, response) => {
  console.log("After action:", action, response)
  // Add any post-processing logic here
}

export const receivedHandler = (dispatch, beforeAction, afterAction) => (action) => {
  if (action.type === receivedTypingData.type) {
    // Dispatch typing data to the Redux store
    dispatch(receivedTypingData(action.payload))
  }
  if (action.status) {
    // Dispatch status data to the Redux store
    dispatch(receivedStatusData(action))
  } else {
    beforeAction?.(action)
    const dispatchResult = dispatch(action)
    if (dispatchResult instanceof Promise) {
      dispatchResult.then((res) => afterAction(action, res))
    } else {
      afterAction(action)
    }
  }
}

export const handleConversationSocket = ({ cable, conversation, role, chanel, setSubscription, dispatch }) => {
  // Check if WebSocket is enabled and cable is available
  if (!cable || [false, "false"].includes(process.env.REACT_APP_WS_ON) || !conversation?.id) {
    return // Early return if conditions are not met
  }
  // Create new conversation subscription
  const conversationSubscription = cable.subscriptions.create(
    { channel: channels.conversationsChannel, conversation_id: conversation?.id },
    {
      received: receivedHandler(dispatch, beforeAction, afterAction),
      connected: () => console.log(`Subscribed to conversation: ${conversation?.id}`),
      disconnected: () => console.log(`Unsubscribed from conversation: ${conversation?.id}`)
    }
  )

  // Create new status subscription
  const statusSubscription = cable.subscriptions.create(
    { channel: channels.statusChannel, contact: role === "user" ? conversation?.contact_id : conversation?.client_id },
    {
      received: receivedHandler(dispatch, beforeAction, afterAction),
      connected: () => console.log(`Subscribed to status: ${conversation?.id}`),
      disconnected: () => console.log(`Unsubscribed from status: ${conversation?.id}`)
    }
  )

  // Save the latest subscriptions
  chanel.current = {
    conversationSubscription,
    statusSubscription
  }

  // Set parent-level state
  setSubscription({
    conversationSubscription,
    statusSubscription
  })
}

export const normalizeSendDate = (dateString) => {
  const date = moment(dateString)
  const now = moment()
  if (date.isSame(now, "day")) {
    return "Today"
  }
  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return "Yesterday"
  }
  if (now.diff(date, "days") < 7) {
    return date.format("dddd") // e.g., Wednesday
  }
  return date.format("MMMM D") // e.g., September 26
}

export const normalizeSendDateAndTime = (dateString) => {
  const date = moment(dateString)
  const now = moment()
  if (date.isSame(now, "day")) {
    return `Today ${date.format("h:mm A")}`
  }
  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday ${date.format("h:mm A")}`
  }
  if (now.diff(date, "days") < 7) {
    return `${date.format("dddd")} ${date.format("h:mm A")}`
  }
  return `${date.format("MMMM D")} ${date.format("h:mm A")}`
}

export const htmlPreviewText = (html) => {
  // Replace <br>, <br/>, <br /> with a space
  let withSpaces = html.replace(/<br\s*\/?/gi, " ")
  // Replace <div> and </div> with a space
  withSpaces = withSpaces.replace(/<\/?div\s*\/?>/gi, " ")
  // replace <p> and </p> with a space as well
  withSpaces = withSpaces.replace(/<\/?p\s*\/?>/gi, " ")
  // Sanitize to plain text
  const sanitized = DOMPurify.sanitize(withSpaces, { ALLOWED_TAGS: [] })
  // Decode HTML entities
  const txt = document.createElement("textarea")
  txt.innerHTML = sanitized
  // Remove standalone > characters
  let value = txt.value.replace(/>/g, " ")
  // Collapse multiple whitespace and trim
  return value.replace(/\s+/g, " ").trim()
}
